import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { JobindsatsElement } from '../core/jobindsats-element';
import { DropDownOption } from './ji-dropdown-element';


@customElement('lit-pagination')
export class LitPagination extends JobindsatsElement(LitElement) {
    static styles = css`
        :host {
            display: block;
        }
        :host span {
            margin: 0px 4px;
        }
        div.paginator-page-container {
            display: flex;
            justify-content: center;
            gap: 5px;
            @apply --layout-horizontal;
            @apply --layout-center;
            @apply --layout-center-justified;
            @apply --layout-center-center;
        }
        div.paginator-page-container.hidden {
            display: none;
        }

        .dropdowns {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        }
        .dd-content {
            width: 200px;
        }

        .dd-pages {
            width: 150px;
        }

        .page-button {
            border: none;
            background-color: transparent;
            cursor: pointer;
            display: flex;
            align-items: center;
        }
        .page-button[disabled] {
            cursor: auto;
        }
        .page-button[disabled] * {
            stroke: var(--main-gray-25);
            cursor: auto;
        }

        .page-number {
            border: none;
            background-color: transparent;
            border-radius: 50%;
            font-weight: bold;
            color: var(--main-blue);
            cursor: pointer;
            height: 25px;
            width: 25px;
        }
        .page-number[disabled] {
            background-color: var(--main-blue);
            color: #FFFFFF;
            cursor: auto;
        }

    `;  

    @state()
    searchParams: URLSearchParams = new URLSearchParams(window.location.search);
    @state()
    content: string[] | null;

    @state()
    selectedContent: string = '';

    @property({ type: Object })
    contentPages: Map<string, string> = new Map();

/** Per-page limit of the elements. */
    @property({ type: Number, reflect: true })
    limit = 10;
/** Total count of the elements. */
    @property({ type: Number, reflect: true })
    total?: number;
/** Current page. */
    @property({ type: Number, reflect: true })
    page = 1;
/** Count of the pages displayed before or after the current page. */
    @property({ type: Number, reflect: true })
    size = 10;
/** Number of paginated pages. */
    @state()
    pages: number = 1;
/** Has pages before the current page. */
    @state()
    hasBefore?: Boolean;
/** Has pages after the current page. */
    @state()
    hasNext?: boolean;
/** Has pages. */
    @state()
    hasPages?: boolean;
/** Displayed page elements */
    @state()
    items = new Array<number>();

    @state()
    contentOptions: DropDownOption[] = [];

    @state()
    pageSizeOptions: DropDownOption[] = [];

    constructor() {
        super();
        this.content = this.searchParams.has('content') ? JSON.parse(this.searchParams.get('content') as string) : null;
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.total) {
            this.observePageCount(this.page, this.limit, this.total);
            this.hasBefore = this.computeBefore(this.page, this.pages);
            this.hasNext = this.computeNext(this.page, this.pages);
            this.hasPages = this.computeHasPage(this.items.length, this.total);
        }
        if (this.searchParams.get('query') === 'alleNyheder' && this.content?.length == undefined) {
            this.content = ['nyhederSide'];
        }

        this.contentOptions = this.convertObject(this.contentPages);
        if (this.content) {
            this.selectedContent = this.content[0]
        }
        else {
            this.selectedContent = 'Alle';
        }

        this.pageSizeOptions.push({ key: '10', selected: this.limit === 10, tabName: '10 per side' });
        this.pageSizeOptions.push({ key: '25', selected: this.limit === 25, tabName: '25 per side' });
        this.pageSizeOptions.push({ key: '50', selected: this.limit === 50, tabName: '50 per side' });
    }

    render() {

        return html`
    
        <div class="dropdowns">
            <div class="dd-content">
                <ji-dropdown-element .options=${this.contentOptions} selected-period=${this.selectedContent} @option-selected=${this.onChangeContent}></ji-dropdown-element>
            </div>
            <div class="dd-pages">
                <ji-dropdown-element .options=${this.pageSizeOptions} selected-period=${this.limit.toString()} @option-selected=${this.onChangeMaxResults}></ji-dropdown-element>
            </div>
        </div>
        <slot></slot>
        
        <div class="paginator-page-container ${!this.hasPages ? 'hidden' : ''}" >
            <button aria-label="Gå en side tilbage" class="page-button" @click="${() => this.onBefore()}" ?disabled="${!this.hasBefore}">
                <svg width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 18L0.999999 9.5L10 0.999998" stroke="#003087" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>

            ${this.items.map(item => html`
                <button class="page-number" raised="${!this.isCurrent(item, this.page)}" ?disabled="${this.isCurrent(item, this.page)}" @click="${() => this.onChange(item)}">
                    ${item}
                </button>
            `)}
            <button aria-label="Gå en side frem" class="page-button" @click=${() => this.onNext()} ?disabled="${!this.hasNext}">
                <svg width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L10 9.5L1 18" stroke="#003087" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        </div>
        `;
    }


    computeBefore(page: number, pages: number) {
        return page > 1;
    }

    computeNext(page: number, pages: number) {
        return page < pages;
    }

    computeHasPage(itemsLength: number, total: number) {
        return itemsLength <= total;
    }

    observePageCount(page: number, limit: number, total: number) {
        if (limit && total) {
            this.pages = Math.ceil(total / limit);
        }

        if (page && limit && total && this.pages > 1) {
            const lastIndex = this.lastIndex(page, this.size);
            if (lastIndex) {
                const items: number[] = [];
                const firstIndex = this.firstIndex(page, this.size);

                for (let num = firstIndex; num <= lastIndex; num++) {
                    items.push(num);
                }
                this.items = items;
            }

        }
    }

    onChangeMaxResults(ev: CustomEvent) {
        if (this.shadowRoot) {
            const maxRes = ev.detail.key;
            if (maxRes !== '10') {
                this.searchParams.has('pageSize')
                    ? this.searchParams.set('pageSize', maxRes)
                    : this.searchParams.append('pageSize', maxRes);
            } else {
                this.searchParams.delete('pageSize');
            }
            if (this.limit.toString() !== maxRes) {
                window.location.search = this.searchParams.toString();
            }
        }
    }

    onChangeContent(ev?: CustomEvent) {
        if (this.content) {
            if (ev?.detail.key !== this.content[0]) {
                this.runSearch(ev?.detail.key);
            }
        }
        else if (ev?.detail.key !== 'Alle')  {
            this.runSearch(ev?.detail.key);
        }
    }

    runSearch(key: string) {
        const selectedContent = key;
        if (key === 'Alle') {
            this.searchParams.delete('content');
        }
        else {
            this.searchParams.set('content', JSON.stringify([selectedContent]));
        }

        window.location.search = this.searchParams.toString();
    }

    onPageChange(page: number) {
        if (page !== 1) {
            this.searchParams.has('page')
                ? this.searchParams.set('page', page.toString())
                : this.searchParams.append('page', page.toString());

        } else {
            this.searchParams.delete('page');
        }
        window.location.search = this.searchParams.toString();
    }

    firstIndex(page: number, size: number) {
        const index = page - size;
        if (index < 1) {
            return 1;
        } else {
            return index;
        }
    }

    lastIndex(page: number, size: number) {
        const index = page + size;
        if (index > this.pages) {
            return this.pages;
        } else {
            return index;
        }
    }

    isCurrent(index: number, page: number) {
        return index === page;
    }

    onChange(item: number) {
        this.page = item;
        this.onPageChange(this.page);
    }

    onBefore() {
        this.page = this.page > 0 ? this.page - 1 : 1;
        this.onPageChange(this.page);
    }

    onNext() {
        this.page = this.page < this.pages ? this.page + 1 : this.pages;
        this.onPageChange(this.page);
    }

    onBegin() {
        this.page = 1;
        this.onPageChange(this.page);
    }

    onEnd() {
        this.page = this.pages;
        this.onPageChange(this.page);
    }

    convertObject(object: Object) {
        var dropdownoptions: DropDownOption[] = [];
        dropdownoptions.push({ key: 'Alle', selected: this.selectedContent === 'Alle', tabName: 'Alle kategorier' });

        Object.entries(object).map(([key, value]) => {
            const option: DropDownOption = { key: key, selected: value === this.selectedContent, tabName: value }
            dropdownoptions.push(option);
        });
        return dropdownoptions;
    }
}